import type {
  StormcloudVideoPlayerConfig,
  Scte35Marker,
  AdController,
} from "../types";
import {
  buildAnalyticsContext,
  sendAdLoadedTracking,
  sendAdImpressionTracking,
} from "../utils/tracking";
import type { AdTimingService } from "./AdTimingService";
import type { AdConfigManager } from "./AdConfigManager";
import type { Scte35CueManager } from "./Scte35CueManager";
import type { PlaceholderLayer } from "./PlaceholderLayer";
import { getAdAudioVolume } from "./PlayerControls";

export interface AdBreakOrchestratorHost {
  readonly config: StormcloudVideoPlayerConfig;
  readonly video: HTMLVideoElement;
  getAdPlayer(): AdController;
  setAdPlayer(player: AdController): void;
  createAdPlayer(continueLiveStreamDuringAds: boolean): AdController;
  shouldContinueLiveStreamDuringAds(): boolean;
  isLiveStream(): boolean;
  getLiveSyncPosition(): number | undefined;
  restartHlsLoad(position: number): void;
  reloadLiveStream(): void;
  generatePodVastUrl(baseUrl: string, breakDurationMs?: number): string;
}

export class AdBreakOrchestrator {
  inAdBreak = false;
  showAds: boolean = false;
  isShowingPlaceholder: boolean = false;
  adDetectSentForCurrentBreak: boolean = false;

  private continuousFetchingActive: boolean = false;
  private continuousFetchLoopRunning = false;
  adRequestQueue: string[] = [];
  adPodQueue: string[] = [];
  private readonly maxPlaceholderDurationMs: number = 5000;

  pendingAdBreak: {
    marker: Scte35Marker;
    detectedAtFragmentSn?: number;
    vastUrls: string[];
    isFetching: boolean;
    fetchStartTime?: number;
  } | null = null;
  prefetchTimerId: number | undefined;

  private readonly host: AdBreakOrchestratorHost;
  private readonly timing: AdTimingService;
  private readonly adConfig: AdConfigManager;
  private readonly cueManager: Scte35CueManager;
  private readonly placeholder: PlaceholderLayer;

  constructor(
    host: AdBreakOrchestratorHost,
    timing: AdTimingService,
    adConfig: AdConfigManager,
    cueManager: Scte35CueManager,
    placeholder: PlaceholderLayer
  ) {
    this.host = host;
    this.timing = timing;
    this.adConfig = adConfig;
    this.cueManager = cueManager;
    this.placeholder = placeholder;
  }

  private get debug(): boolean {
    return !!this.host.config.debugAdTiming;
  }

  private get analyticsContext() {
    return buildAnalyticsContext(this.host.config);
  }

  // ───────────────────────────────────────────────────────────────
  //  IMA event listeners
  // ───────────────────────────────────────────────────────────────

  private nextAdRequestUrl(baseVastUrl: string): string {
    const remaining = Math.min(
      this.timing.getWallClockRemainingAdMs(),
      this.timing.getDurationBudgetRemainingMs()
    );
    const breakDurationMs = remaining > 0 ? remaining : undefined;
    return this.host.generatePodVastUrl(baseVastUrl, breakDurationMs);
  }

  attachImaEventListeners(): void {
    const adPlayer = this.host.getAdPlayer();
    adPlayer.on("pod_ad_started", (payload?: any) => {
      this.timing.notePodAdStarted();
      if (this.debug) {
        console.log(
          `[POD] Playing pod ad ${payload?.index != null ? payload.index + 1 : "?"}/${payload?.total ?? "?"}`
        );
      }
    });
    adPlayer.on("all_ads_completed", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();

      this.timing.consecutiveFailures = 0;

      if (this.debug) {
        console.log(
          `[StormcloudVideoPlayer] all_ads_completed event received - remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }

      if (this.inAdBreak) {
        if (this.timing.hasTimeToStartAnotherAd()) {
          this.tryNextAvailableAdWithRateLimit();
        } else {
          this.handleAdPodComplete();
        }
      }
    });
    adPlayer.on("ad_error", (errorPayload?: any) => {
      let errorMessage = "Ad playback failed";

      if (errorPayload) {
        const errorCode = errorPayload.code || errorPayload.errorCode || "unknown";
        const vastErrorCode = errorPayload.vastErrorCode;
        const message = errorPayload.message || errorPayload.errorMessage || "Unknown error";
        const cause = errorPayload.cause || errorPayload.innerError || errorPayload.error;

        errorMessage = `Ad error: AdError ${errorCode}: ${message}`;

        if (vastErrorCode && vastErrorCode !== "N/A" && vastErrorCode !== errorCode) {
          errorMessage += ` (VAST Error Code: ${vastErrorCode})`;
        }

        if (cause) {
          const causeMessage = typeof cause === "string" ? cause : (cause.message || String(cause));
          errorMessage += `. Caused by: ${causeMessage}`;
        }
      }

      console.error("[AD-ERROR]", errorMessage, errorPayload || "");
      this.handleAdFailure();
    });
    adPlayer.on("content_pause", () => {
      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = true;
      this.timing.resetGamNoFillCounter();

      if (this.inAdBreak && this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
        this.timing.scheduleAdStopAtBreakBoundary();
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Starting ad break timer on content_pause (first ad starting)");
        }
      }

      this.host.getAdPlayer().setAdVolume(getAdAudioVolume(this.host.getAdPlayer()));

      if (this.isShowingPlaceholder) {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Hiding placeholder - new ads starting");
        }
        this.host.getAdPlayer().hidePlaceholder();
        this.isShowingPlaceholder = false;
      }
    });
    adPlayer.on("content_resume", () => {
      sendAdImpressionTracking(
        this.host.config.licenseKey,
        { source: "hls", adIndex: this.timing.currentAdIndex, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      if (!this.host.video.muted) {
        this.host.video.muted = true;
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Muted video on content_resume (ad ended, gap between ads)");
        }
      }

      this.timing.clearAdFailsafeTimer();
      this.timing.clearAdRequestWatchdog();
      this.timing.activeAdRequestToken = null;
      this.showAds = false;

      if (!this.inAdBreak) {
        return;
      }

      this.timing.consecutiveFailures = 0;

      this.timing.recordPlayedAdDuration(this.host.getAdPlayer());
      const remaining = this.timing.getRemainingAdMs();

      if (this.debug) {
        console.log(
          `[CONTINUOUS-FETCH] content_resume event: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}`,
          this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
        );
      }

      if (this.timing.hasTimeToStartAnotherAd()) {
        this.tryNextAvailableAdWithRateLimit();
      } else {
        this.handleAdPodComplete();
      }
    });
  }

  recreateAdController(): void {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Recreating AdController for new ad");
    }

    const shouldShowPlaceholder = this.inAdBreak && this.showAds;
    const adPlayer = this.host.getAdPlayer();
    if (shouldShowPlaceholder && adPlayer) {
      if (this.debug) {
        console.log("[StormcloudVideoPlayer] Showing placeholder before destroying old AdController");
      }
      this.placeholder.show();
      adPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }

    const preservedMutedState = adPlayer.getOriginalMutedState();
    const preservedVolumeState = adPlayer.getOriginalVolume();

    if (adPlayer) {
      try {
        adPlayer.destroy();
        this.host.video.muted = true;
      } catch (error) {
        if (this.debug) {
          console.warn("[StormcloudVideoPlayer] Error destroying old AdController:", error);
        }
      }
    }

    const continueLiveStreamDuringAds = this.host.shouldContinueLiveStreamDuringAds();
    const newAdPlayer = this.host.createAdPlayer(continueLiveStreamDuringAds);
    this.host.setAdPlayer(newAdPlayer);

    newAdPlayer.initialize();
    newAdPlayer.updateOriginalMutedState(preservedMutedState, preservedVolumeState);
    this.attachImaEventListeners();

    if (shouldShowPlaceholder) {
      this.placeholder.show();
      newAdPlayer.showPlaceholder();
      this.isShowingPlaceholder = true;
    }
  }

  clearPendingAdBreak(): void {
    if (this.prefetchTimerId != null) {
      clearTimeout(this.prefetchTimerId);
      this.prefetchTimerId = undefined;
    }
    this.pendingAdBreak = null;
    this.cueManager.pendingScte35CueKey = undefined;
    this.adConfig.podAssignedByPrefetch = false;
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad break start
  // ───────────────────────────────────────────────────────────────

  async handleAdStart(_marker: Scte35Marker): Promise<void> {
    const scheduled = this.adConfig.findCurrentOrNextBreak(
      this.host.video.currentTime * 1000
    );
    if (scheduled) {
      this.adConfig.consumedVmapBreakIds.add(this.adConfig.getAdBreakKey(scheduled));
    }
    const tags = this.adConfig.selectVastTagsForBreak(scheduled);

    let baseVastUrl: string;

    if (tags && tags.length > 0 && tags[0]) {
      baseVastUrl = tags[0];
    } else if (this.adConfig.apiVastTagUrl) {
      baseVastUrl = this.adConfig.apiVastTagUrl;
    } else {
      this.clearPendingAdBreak();
      return;
    }

    const adBreakDurationMs =
      _marker.durationSeconds != null
        ? _marker.durationSeconds * 1000
        : scheduled?.durationMs;

    if (this.debug) {
      const mode = this.host.config.isLiveStream ? "LIVE" : "VOD";
      console.log(
        `[CONTINUOUS-FETCH] ${mode} MODE: Target duration=${adBreakDurationMs}ms`
      );
    }

    this.timing.failedVastUrls.clear();
    this.timing.temporaryFailureUrls.clear();
    this.timing.consecutiveFailures = 0;
    this.continuousFetchingActive = true;
    this.isShowingPlaceholder = false;
    this.timing.totalAdRequestsInBreak = 0;

    if (this.adConfig.podAssignedByPrefetch) {
      this.adConfig.podAssignedByPrefetch = false;
    } else {
      this.adConfig.beginNewAdPod();
    }

    const currentMuted = this.host.video.muted;
    const currentVolume = this.host.video.volume;
    this.host.getAdPlayer().updateOriginalMutedState(currentMuted, currentVolume);

    this.inAdBreak = true;
    this.timing.adBreakPlayedDurationMs = 0;
    this.timing.currentAdIndex = 0;
    this.timing.totalAdsInBreak = 0;
    this.adPodQueue = [];
    this.showAds = false;

    if (
      this.timing.expectedAdBreakDurationMs == null &&
      adBreakDurationMs != null
    ) {
      this.timing.expectedAdBreakDurationMs = adBreakDurationMs;
    }

    const podPlayed = await this.startOptimizedPod(
      baseVastUrl,
      adBreakDurationMs,
      currentMuted,
      currentVolume
    );

    if (!podPlayed) {
      if (this.debug) {
        console.log("[POD] Initial pod request did not fill; attempting top-up pod request");
      }
      this.continuousFetchingActive = true;
      this.startContinuousFetching(baseVastUrl);
      await this.tryNextAvailableAdWithRateLimit();
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Optimized pod (single multi-ad request)
  // ───────────────────────────────────────────────────────────────

  private async startOptimizedPod(
    baseVastUrl: string,
    breakDurationMs: number | undefined,
    currentMuted: boolean,
    currentVolume: number
  ): Promise<boolean> {
    let podUrl: string | undefined;
    if (this.pendingAdBreak && this.pendingAdBreak.vastUrls.length > 0) {
      podUrl = this.pendingAdBreak.vastUrls[0];
      this.clearPendingAdBreak();
    } else {
      podUrl = this.host.generatePodVastUrl(baseVastUrl, breakDurationMs);
    }

    if (!podUrl) {
      return false;
    }

    this.continuousFetchingActive = false;

    this.recreateAdController();

    const adPlayer = this.host.getAdPlayer();
    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);

    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      await adPlayer.requestAds(podUrl);
      this.timing.clearAdRequestWatchdog();

      if (this.timing.activeAdRequestToken !== requestToken) {
        return true;
      }

      const podCount = adPlayer.getPodAdCount?.() ?? 1;
      if (podCount < 1) {
        return false;
      }

      if (this.debug) {
        console.log(
          podCount === 1
            ? "[POD] Response contained a single ad; playing it and enabling pod top-up"
            : `[POD] Optimized pod returned ${podCount} ads; playing pod in one controller`
        );
      }

      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: podUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.startAdFailsafeTimer(requestToken);
      await adPlayer.play();
      this.timing.markAdStarted();

      if (
        this.timing.expectedAdBreakDurationMs != null &&
        this.timing.getAdBreakEndWallClockMs() == null
      ) {
        this.timing.scheduleAdStopAtBreakBoundary();
      }

      adPlayer.setAdVolume(currentMuted ? 0 : currentVolume);
      this.timing.consecutiveFailures = 0;

      this.continuousFetchingActive = true;
      this.startContinuousFetching(baseVastUrl);

      return true;
    } catch (error) {
      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      if (this.debug) {
        console.warn("[POD] Optimized pod request/play failed:", error);
      }

      if (this.timing.isTemporaryAdError(error as Error)) {
        this.timing.temporaryFailureUrls.set(podUrl, Date.now());
      }

      this.continuousFetchingActive = true;
      this.recreateAdController();
      return false;
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Continuous fetching
  // ───────────────────────────────────────────────────────────────

  private startContinuousFetching(baseVastUrl: string): void {
    if (!this.continuousFetchingActive) {
      return;
    }

    if (this.continuousFetchLoopRunning) {
      if (this.debug) {
        console.log("[CONTINUOUS-FETCH] Fetch loop already running, skipping duplicate start");
      }
      return;
    }

    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Starting continuous ad fetching loop");
    }

    this.continuousFetchLoopRunning = true;
    this.continuousFetchLoop(baseVastUrl);
  }

  private async continuousFetchLoop(baseVastUrl: string): Promise<void> {
    const epoch = this.timing.adBreakEpoch;
    while (
      this.continuousFetchingActive &&
      this.inAdBreak &&
      epoch === this.timing.adBreakEpoch
    ) {
      const remaining = Math.min(
        this.timing.getWallClockRemainingAdMs(),
        this.timing.getDurationBudgetRemainingMs()
      );

      if (remaining <= this.timing.getMinRemainingMsToStartAd()) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Not enough ad break time left (wall=${this.timing.getWallClockRemainingAdMs()}ms, budget=${this.timing.getDurationBudgetRemainingMs()}ms), stopping URL generation`);
        }
        break;
      }

      if (this.timing.isGamInCooldown()) {
        if (this.debug) {
          console.log("[CIRCUIT-BREAKER] GAM in cooldown, stopping URL generation");
        }
        break;
      }

      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), stopping URL generation`);
        }
        break;
      }

      if (this.timing.totalAdRequestsInBreak >= this.timing.maxTotalAdRequestsPerBreak) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] Maximum ad requests reached (${this.timing.maxTotalAdRequestsPerBreak}), stopping`);
        }
        break;
      }

      const maxQueueSize = 5;
      if (this.adRequestQueue.length >= maxQueueSize) {
        if (this.debug) {
          console.log(`[CONTINUOUS-FETCH] URL queue full (${this.adRequestQueue.length}), waiting...`);
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        continue;
      }

      const newAdUrl = this.nextAdRequestUrl(baseVastUrl);

      if (!newAdUrl || this.timing.failedVastUrls.has(newAdUrl) || this.timing.isUrlInCooldown(newAdUrl)) {
        await new Promise(resolve => setTimeout(resolve, 500));
        continue;
      }

      if (this.debug) {
        const queuedUrlsPreview = [...this.adRequestQueue, newAdUrl];
        console.log(
          `[CONTINUOUS-FETCH] Queued VAST URL (queue size: ${queuedUrlsPreview.length})`,
          { queuedUrls: queuedUrlsPreview }
        );
      }

      this.adRequestQueue.push(newAdUrl);

      const generationDelay = this.timing.consecutiveFailures > 0
        ? Math.min(1000 * Math.pow(2, this.timing.consecutiveFailures), 5000)
        : 500;
      await new Promise(resolve => setTimeout(resolve, generationDelay));
    }

    this.continuousFetchLoopRunning = false;

    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] URL generation loop ended (queued: ${this.adRequestQueue.length})`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }
  }

  stopContinuousFetching(): void {
    this.continuousFetchingActive = false;
    this.continuousFetchLoopRunning = false;

    this.placeholder.hide();

    if (this.debug) {
      console.log("[CONTINUOUS-FETCH] Stopping continuous ad fetching");
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad iteration
  // ───────────────────────────────────────────────────────────────

  async tryNextAvailableAdWithRateLimit(): Promise<void> {
    if (!this.inAdBreak) {
      return;
    }

    const epoch = this.timing.adBreakEpoch;

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      }
      this.handleAdPodComplete();
      return;
    }

    const backoffMultiplier = Math.pow(2, this.timing.consecutiveFailures);
    const backoffDelay = Math.min(this.timing.backoffBaseMs * backoffMultiplier, this.timing.maxBackoffMs);
    const effectiveMinInterval = this.timing.minAdRequestIntervalMs + (this.timing.consecutiveFailures > 0 ? backoffDelay : 0);

    const timeSinceLastRequest = Date.now() - this.timing.lastAdRequestTime;
    if (timeSinceLastRequest < effectiveMinInterval) {
      const waitTime = effectiveMinInterval - timeSinceLastRequest;
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Rate limiting: waiting ${waitTime}ms before next request (backoff: ${this.timing.consecutiveFailures} failures)`);
      }
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    if (!this.inAdBreak || epoch !== this.timing.adBreakEpoch) {
      return;
    }

    return this.tryNextAvailableAd(0);
  }

  private async tryNextAvailableAd(retryCount: number = 0): Promise<void> {
    if (!this.inAdBreak) {
      return;
    }

    const remaining = this.timing.getRemainingAdMs();

    if (!this.timing.hasTimeToStartAnotherAd()) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Not enough time remaining (${remaining}ms) to start another ad, ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.isGamInCooldown()) {
      if (this.debug) {
        console.log("[CIRCUIT-BREAKER] GAM in cooldown, ending ad break gracefully");
      }
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Too many consecutive failures (${this.timing.consecutiveFailures}), ending ad break`);
      }
      this.handleAdPodComplete();
      return;
    }

    this.timing.cleanupTemporaryFailures();

    while (this.adRequestQueue.length > 0) {
      const nextAdUrl = this.adRequestQueue.shift();

      if (!nextAdUrl) continue;

      if (this.timing.failedVastUrls.has(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping permanently-failed URL, trying next...");
        continue;
      }

      if (this.timing.isUrlInCooldown(nextAdUrl)) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping URL in cooldown (recent no-fill), trying next...");
        continue;
      }

      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Requesting next ad via HLS ad player (${this.timing.currentAdIndex + 1}, ${this.adRequestQueue.length} remaining in queue)`);
      }

      try {
        await this.playSingleAd(nextAdUrl);
        this.timing.consecutiveFailures = 0;
      } catch (error) {
        const errorMessage = (error as Error).message;
        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad request failed:", errorMessage);

        if (errorMessage.includes("ad_duration_exceeds_budget")) {
          await this.tryNextAvailableAdWithRateLimit();
          return;
        }

        if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
          this.timing.consecutiveFailures++;
        }

        await this.tryNextAvailableAdWithRateLimit();
      }
      return;
    }

    const maxRetries = 3;
    if (this.continuousFetchingActive && retryCount < maxRetries && remaining > 2000) {
      if (this.debug) {
        console.log(`[CONTINUOUS-FETCH] Queue empty, waiting for URLs... (${retryCount + 1}/${maxRetries})`);
      }
      await new Promise(resolve => setTimeout(resolve, 500));
      await this.tryNextAvailableAd(retryCount + 1);
      return;
    }

    if (!this.isShowingPlaceholder && remaining > 1000) {
      this.showPlaceholderAndWaitForAds();
    } else {
      if (this.debug) console.log("[CONTINUOUS-FETCH] No more ads available, ending ad break");
      this.handleAdPodComplete();
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Single ad playback
  // ───────────────────────────────────────────────────────────────

  async playSingleAd(vastTagUrl: string): Promise<void> {
    const adPlayer = this.host.getAdPlayer();
    if (adPlayer.isAdPlaying()) return;

    if (this.timing.failedVastUrls.has(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping permanently-failed VAST URL:", vastTagUrl);
      throw new Error("VAST URL permanently failed");
    }

    if (this.timing.isUrlInCooldown(vastTagUrl)) {
      if (this.debug) console.warn("[AD-ERROR] Skipping URL in cooldown (recent no-fill):", vastTagUrl);
      throw new Error("VAST URL in cooldown after no-fill");
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.warn("[AD-ERROR] Too many consecutive failures, not requesting more ads");
      throw new Error("Too many consecutive failures");
    }

    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.warn("[CIRCUIT-BREAKER] GAM in cooldown, skipping ad request");
      throw new Error("GAM in cooldown");
    }

    this.recreateAdController();

    const requestToken = ++this.timing.adRequestTokenCounter;
    this.timing.activeAdRequestToken = requestToken;
    this.timing.startAdRequestWatchdog(requestToken);

    try {
      await this.timing.enforceGlobalRateLimit();
      this.timing.lastAdRequestTime = Date.now();
      const ap = this.host.getAdPlayer();
      await ap.requestAds(vastTagUrl);
      sendAdLoadedTracking(
        this.host.config.licenseKey,
        { source: "hls", vastUrl: vastTagUrl, timestamp: new Date().toISOString() },
        this.analyticsContext
      ).catch(() => {});

      this.timing.clearAdRequestWatchdog();

      if (this.timing.activeAdRequestToken !== requestToken) return;

      if (!this.timing.ensureLoadedAdFitsBudget(ap)) {
        this.timing.rejectLoadedAdForDuration(ap);
        this.recreateAdController();
        throw new Error("ad_duration_exceeds_budget");
      }

      try {
        this.timing.startAdFailsafeTimer(requestToken);
        await ap.play();
        this.timing.markAdStarted();

        if (this.timing.expectedAdBreakDurationMs != null && this.timing.getAdBreakEndWallClockMs() == null) {
          this.timing.scheduleAdStopAtBreakBoundary();
        }

        ap.setAdVolume(getAdAudioVolume(ap));
        this.timing.consecutiveFailures = 0;
        this.timing.temporaryFailureUrls.delete(vastTagUrl);
      } catch (playError) {
        if (this.debug) console.error("[AD-ERROR] Failed to play ad:", playError);

        if (this.timing.isTemporaryAdError(playError as Error)) {
          this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
          if (this.debug) console.log("[AD-ERROR] Temporary play error - URL can be retried after cooldown");
        } else {
          this.timing.failedVastUrls.add(vastTagUrl);
          if (this.debug) console.log("[AD-ERROR] Permanent play error - URL blacklisted");
        }

        this.timing.clearAdFailsafeTimer();
        if (this.timing.activeAdRequestToken === requestToken) {
          this.timing.activeAdRequestToken = null;
        }
        throw playError;
      }
    } catch (error) {
      const errorMessage = (error as Error)?.message || "";
      if (this.debug) console.error("[AD-ERROR] Ad request failed:", errorMessage);

      if (this.timing.isTemporaryAdError(error as Error)) {
        this.timing.temporaryFailureUrls.set(vastTagUrl, Date.now());
        if (this.debug) console.log("[AD-ERROR] Temporary error (no-fill/timeout) - URL can be retried after cooldown");
      } else {
        this.timing.failedVastUrls.add(vastTagUrl);
        if (this.debug) console.log("[AD-ERROR] Permanent error - URL blacklisted:", errorMessage);
      }

      this.timing.clearAdRequestWatchdog();
      this.timing.clearAdFailsafeTimer();
      if (this.timing.activeAdRequestToken === requestToken) {
        this.timing.activeAdRequestToken = null;
      }
      throw error;
    }
  }

  // ───────────────────────────────────────────────────────────────
  //  Placeholder wait
  // ───────────────────────────────────────────────────────────────

  private async showPlaceholderAndWaitForAds(): Promise<void> {
    const remaining = this.timing.getRemainingAdMs();
    const waitTime = Math.min(this.maxPlaceholderDurationMs, remaining);

    if (this.timing.isGamInCooldown()) {
      if (this.debug) console.log("[CIRCUIT-BREAKER] GAM in cooldown, skipping placeholder wait");
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log("[CONTINUOUS-FETCH] Skipping placeholder - too many consecutive failures");
      this.handleAdPodComplete();
      return;
    }

    if (waitTime < 1000) {
      this.handleAdPodComplete();
      return;
    }

    if (this.debug) {
      console.log(`[CONTINUOUS-FETCH] Showing placeholder for ${waitTime}ms while waiting for ad URLs`);
    }

    this.isShowingPlaceholder = true;
    this.host.getAdPlayer().showPlaceholder();

    const checkInterval = 300;
    const maxChecks = Math.floor(waitTime / checkInterval);

    for (let i = 0; i < maxChecks; i++) {
      await new Promise(resolve => setTimeout(resolve, checkInterval));

      if (!this.inAdBreak) return;

      if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Too many failures during placeholder wait");
        break;
      }

      if (!this.timing.hasTimeToStartAnotherAd()) {
        if (this.debug) console.log("[CONTINUOUS-FETCH] Not enough time remaining during placeholder wait, ending ad break");
        break;
      }

      while (this.adRequestQueue.length > 0) {
        if (!this.timing.hasTimeToStartAnotherAd()) break;

        const nextAdUrl = this.adRequestQueue.shift();
        if (!nextAdUrl) continue;
        if (this.timing.failedVastUrls.has(nextAdUrl)) continue;
        if (this.timing.isUrlInCooldown(nextAdUrl)) continue;

        if (this.debug) console.log("[CONTINUOUS-FETCH] Ad URL available, requesting via HLS ad player");

        this.isShowingPlaceholder = false;
        this.host.getAdPlayer().hidePlaceholder();

        try {
          await this.playSingleAd(nextAdUrl);
          this.timing.consecutiveFailures = 0;
        } catch (error) {
          const errorMessage = (error as Error).message;
          if (errorMessage.includes("ad_duration_exceeds_budget")) {
            await this.tryNextAvailableAdWithRateLimit();
            return;
          }
          if (!errorMessage.includes("permanently failed") && !errorMessage.includes("in cooldown")) {
            this.timing.consecutiveFailures++;
          }
          await this.tryNextAvailableAdWithRateLimit();
        }
        return;
      }
    }

    if (this.debug) console.log("[CONTINUOUS-FETCH] Placeholder timeout, ending ad break");

    this.isShowingPlaceholder = false;
    this.host.getAdPlayer().hidePlaceholder();
    this.handleAdPodComplete();
  }

  // ───────────────────────────────────────────────────────────────
  //  Ad break completion
  // ───────────────────────────────────────────────────────────────

  handleAdPodComplete(): void {
    if (this.debug) {
      console.log("[StormcloudVideoPlayer] Ad pod complete - cleaning up");
    }

    this.timing.resetForBreakEnd();

    this.stopContinuousFetching();
    this.clearPendingAdBreak();

    if (this.isShowingPlaceholder) {
      this.host.getAdPlayer().hidePlaceholder();
      this.isShowingPlaceholder = false;
    }

    this.adRequestQueue = [];

    this.inAdBreak = false;
    this.adDetectSentForCurrentBreak = false;
    this.cueManager.activeScte35BreakKey = undefined;
    this.cueManager.scheduledScte35BreakKey = undefined;
    this.adPodQueue = [];
    this.showAds = false;

    this.host.getAdPlayer().stop().catch(() => {});

    const restoredMuted = this.host.getAdPlayer().getOriginalMutedState();
    const restoredVolume = this.host.getAdPlayer().getOriginalVolume();

    if (this.host.video.muted !== restoredMuted) {
      this.host.video.muted = restoredMuted;
    }
    if (Math.abs(this.host.video.volume - restoredVolume) > 0.01) {
      this.host.video.volume = restoredVolume;
    }

    this.host.video.style.visibility = "visible";
    this.host.video.style.opacity = "1";

    if (this.host.isLiveStream()) {
      const liveSyncPos = this.host.getLiveSyncPosition();
      if (liveSyncPos != null && liveSyncPos > this.host.video.currentTime) {
        if (this.debug) {
          console.log(
            `[StormcloudVideoPlayer] Live break end — snapping to live edge: ${this.host.video.currentTime.toFixed(2)}s → ${liveSyncPos.toFixed(2)}s`
          );
        }
        this.host.video.currentTime = liveSyncPos;
        this.host.restartHlsLoad(liveSyncPos);
      }
      this.resumeContentPlayback();
      this.monitorLiveResumeStall(this.host.video.currentTime);
      return;
    }

    this.resumeContentPlayback();
  }

  private getBufferedAhead(): number {
    const video = this.host.video;
    const t = video.currentTime;
    const ranges = video.buffered;
    for (let i = 0; i < ranges.length; i++) {
      if (t >= ranges.start(i) - 0.5 && t <= ranges.end(i)) {
        return ranges.end(i) - t;
      }
    }
    return 0;
  }

  private monitorLiveResumeStall(resumeBaseline: number): void {
    const pollIntervalMs = 1000;
    const graceMs = 5000;
    const recoveryCooldownMs = 5000;
    const maxRecoveries = 2;
    const maxMonitorMs = 30000;

    const startTime = Date.now();
    let baseline = resumeBaseline;
    let recoveries = 0;
    let lastRecoveryAt = 0;

    const poll = (): void => {
      if (this.inAdBreak) {
        return;
      }

      const video = this.host.video;
      const currentTime = video.currentTime;
      const advanced = currentTime > baseline + 0.5;
      const playing = !video.paused && video.readyState >= 3;

      if (advanced && playing) {
        if (this.debug) {
          console.log("[StormcloudVideoPlayer] Live stream resumed successfully after ad break");
        }
        return;
      }

      if (video.paused) {
        video.play()?.catch(() => {});
      }

      const elapsed = Date.now() - startTime;
      const bufferedAhead = this.getBufferedAhead();
      const wedged = bufferedAhead < 0.5 && !advanced;

      if (elapsed >= maxMonitorMs) {
        if (this.debug && !advanced) {
          console.warn(
            `[StormcloudVideoPlayer] Live stream failed to resume after ${(maxMonitorMs / 1000).toFixed(0)}s (t=${currentTime.toFixed(2)}s, buffered=${bufferedAhead.toFixed(2)}s)`
          );
        }
        return;
      }

      const canRecover =
        wedged &&
        elapsed >= graceMs &&
        Date.now() - lastRecoveryAt >= recoveryCooldownMs &&
        recoveries < maxRecoveries;

      if (canRecover) {
        recoveries++;
        lastRecoveryAt = Date.now();

        const isFinalAttempt = recoveries >= maxRecoveries;

        if (!isFinalAttempt) {
          const liveSyncPos = this.host.getLiveSyncPosition();
          const reloadPos =
            liveSyncPos != null && liveSyncPos > currentTime ? liveSyncPos : currentTime;

          if (this.debug) {
            console.warn(
              `[StormcloudVideoPlayer] Content wedged after ad break (t=${currentTime.toFixed(2)}s, buffered=${bufferedAhead.toFixed(2)}s) — restarting HLS load at ${reloadPos.toFixed(2)}s (recovery ${recoveries}/${maxRecoveries})`
            );
          }

          if (liveSyncPos != null && liveSyncPos > currentTime) {
            video.currentTime = liveSyncPos;
          }
          this.host.restartHlsLoad(reloadPos);
        } else {
          if (this.debug) {
            console.warn(
              `[StormcloudVideoPlayer] Content still wedged after ad break (t=${currentTime.toFixed(2)}s, buffered=${bufferedAhead.toFixed(2)}s) — hard-reloading live stream (recovery ${recoveries}/${maxRecoveries})`
            );
          }
          this.host.reloadLiveStream();
        }

        baseline = video.currentTime;
        if (video.paused) {
          video.play()?.catch(() => {});
        }
      }

      window.setTimeout(poll, pollIntervalMs);
    };

    window.setTimeout(poll, pollIntervalMs);
  }

  private resumeContentPlayback(attempt: number = 0): void {
    const maxAttempts = 3;

    if (!this.host.video.paused) {
      if (this.debug && attempt === 0) {
        console.log("[StormcloudVideoPlayer] Content video already playing after ads");
      }
      return;
    }

    if (this.debug) {
      console.log(
        `[StormcloudVideoPlayer] Resuming content playback after ads (attempt ${attempt + 1}/${maxAttempts})`
      );
    }

    const playResult = this.host.video.play();
    if (playResult && typeof playResult.catch === "function") {
      playResult.catch((error) => {
        if (attempt + 1 >= maxAttempts) {
          if (this.debug) {
            console.warn(
              "[StormcloudVideoPlayer] Failed to resume content playback after ads:",
              error
            );
          }
          return;
        }
        const backoffMs = 250 * (attempt + 1);
        window.setTimeout(() => {
          this.resumeContentPlayback(attempt + 1);
        }, backoffMs);
      });
    }
  }

  handleAdFailure(): void {
    this.timing.consecutiveFailures++;
    this.timing.recordGamNoFill();
    const remaining = this.timing.getRemainingAdMs();

    if (this.debug) {
      console.log(
        `[CONTINUOUS-FETCH] Ad failure: remaining=${remaining}ms, queued ads=${this.adRequestQueue.length}, consecutiveFailures=${this.timing.consecutiveFailures}`,
        this.adRequestQueue.length > 0 ? { queuedUrls: [...this.adRequestQueue] } : {}
      );
    }

    if (this.timing.consecutiveFailures >= this.timing.maxConsecutiveFailures) {
      if (this.debug) console.log(`[CONTINUOUS-FETCH] Max consecutive failures reached (${this.timing.consecutiveFailures}), ending ad break gracefully`);
      this.handleAdPodComplete();
      return;
    }

    if (this.timing.hasTimeToStartAnotherAd()) {
      this.tryNextAvailableAdWithRateLimit();
    } else {
      if (this.debug) console.log("[AD-ERROR] Ad failed and not enough time remaining for another ad. Failed URLs:", this.timing.failedVastUrls.size);
      this.handleAdPodComplete();
    }
  }
}
